import type { User } from "@book-club/shared";
import { USER_LEAVE_TEXT_MAX } from "@book-club/shared";
import { arrayUnion, deleteField, serverTimestamp, Timestamp } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import type {
    CreateMemberInput,
    CreateMemberResult,
    PromoteToAdminInput,
    PromoteToAdminResult,
    UpdateMemberPhoneNumberInput,
    UpdateMemberPhoneNumberResult,
} from "@/features/members/types";
import { functionsService } from "@/infrastructure/firebase/firebase";
import firebaseDatabaseService from "@/infrastructure/firebase/firebaseDatabaseService";
import logger from "@/logger";

/**
 * Fetches all users from Firestore, excluding archived members
 * @returns Array of all non-archived users
 */
async function getAllMembers(): Promise<User[]> {
    logger.debug("[Members] Fetching all members...");

    const allMembers = await firebaseDatabaseService.getDocuments<User>("users");

    // Filter out archived members (defense at service layer)
    const activeMembers = allMembers.filter((member) => !member.archivedAt);

    logger.debug(
        `[Members] Found ${activeMembers.length} active members (${allMembers.length - activeMembers.length} archived)`
    );

    return activeMembers;
}

/**
 * Fetches a single user by ID
 * @param uid - User ID
 * @returns User document or null if not found
 */
async function getMemberById(uid: string): Promise<User | null> {
    logger.debug(`[Members] Fetching member: ${uid}`);

    const member = await firebaseDatabaseService.getDocument<User>("users", uid);

    if (!member) {
        logger.debug(`[Members] Member ${uid} not found`);
        return null;
    }

    logger.debug(`[Members] Found member: ${member.displayName ?? "Unknown"}`);
    return member;
}

/**
 * Revokes access for a member by setting accessRevokedAt timestamp
 * @param uid - User ID
 */
async function revokeAccess(uid: string): Promise<void> {
    logger.debug(`[Members] Revoking access for member: ${uid}`);

    await firebaseDatabaseService.updateDocument<User>("users", uid, {
        accessRevokedAt: serverTimestamp(),
    });

    logger.debug(`[Members] Successfully revoked access for member: ${uid}`);
}

/**
 * Creates a new member via Cloud Function (Auth user + Firestore user document).
 * @param input - Phone number, display name, and whether to grant access immediately
 * @returns Created member uid, phoneNumber, and displayName
 */
async function createMember(input: CreateMemberInput): Promise<CreateMemberResult> {
    logger.debug("[Members] Creating member...");

    const createMemberFunction = httpsCallable<CreateMemberInput, CreateMemberResult>(functionsService, "createMember");
    const result = await createMemberFunction(input);

    logger.debug("[Members] Member created:", result.data.uid);
    return result.data;
}

/**
 * Grants access to a member by setting accessGrantedAt and clearing accessRevokedAt and flaggedForRemovalAt
 * @param uid - User ID
 */
async function grantAccess(uid: string): Promise<void> {
    logger.debug(`[Members] Granting access for member: ${uid}`);

    await firebaseDatabaseService.updateDocument<User>("users", uid, {
        accessGrantedAt: serverTimestamp(),
        accessRevokedAt: deleteField(),
        flaggedForRemovalAt: deleteField(),
    });

    logger.debug(`[Members] Successfully granted access for member: ${uid}`);
}

/**
 * Updates a member's display name in Firestore
 * @param uid - User ID
 * @param displayName - New display name
 */
async function updateMemberDisplayName(uid: string, displayName: string): Promise<void> {
    logger.debug(`[Members] Updating display name for member: ${uid}`);

    await firebaseDatabaseService.updateDocument<User>("users", uid, {
        displayName,
    });

    logger.debug(`[Members] Successfully updated display name for member: ${uid}`);
}

/**
 * Updates a member's phone number via Cloud Function (updates both Firebase Auth and Firestore).
 * Admin-only.
 * @param uid - User ID
 * @param phoneNumber - New phone number (any reasonable format; normalized server-side)
 * @returns Updated uid and normalized phone number
 */
async function updateMemberPhoneNumber(uid: string, phoneNumber: string): Promise<UpdateMemberPhoneNumberResult> {
    logger.debug(`[Members] Updating phone number for member: ${uid}`);

    const updateMemberPhoneNumberFunction = httpsCallable<UpdateMemberPhoneNumberInput, UpdateMemberPhoneNumberResult>(
        functionsService,
        "updateMemberPhoneNumber"
    );
    const result = await updateMemberPhoneNumberFunction({ phoneNumber, uid });

    logger.debug(`[Members] Successfully updated phone number for member: ${uid}`);
    return result.data;
}

/**
 * Updates a member's birthday (month + day) in Firestore
 * @param uid - User ID
 * @param birthday - New birthday object (or null to clear)
 */
async function updateMemberBirthday(uid: string, birthday: { month: number; day: number } | null): Promise<void> {
    logger.debug(`[Members] Updating birthday for member: ${uid}`);

    await firebaseDatabaseService.updateDocument<User>("users", uid, {
        birthday: birthday ?? deleteField(),
    });

    logger.debug(`[Members] Successfully updated birthday for member: ${uid}`);
}

/**
 * Promotes an existing member to a web admin via Cloud Function
 * (sets Auth email/password and Firestore email + adminAccessGrantedAt).
 * Admin-only.
 */
async function promoteToAdmin(input: PromoteToAdminInput): Promise<PromoteToAdminResult> {
    logger.debug(`[Members] Promoting member to admin: ${input.uid}`);

    const promoteToAdminFunction = httpsCallable<PromoteToAdminInput, PromoteToAdminResult>(
        functionsService,
        "promoteToAdmin"
    );
    const result = await promoteToAdminFunction(input);

    logger.debug(`[Members] Successfully promoted member to admin: ${input.uid}`);
    return result.data;
}

/**
 * Archives a member by setting archivedAt timestamp (soft delete)
 * Only available for waitlisted or revoked members
 * @param uid - User ID
 */
async function archiveMember(uid: string): Promise<void> {
    logger.debug(`[Members] Archiving member: ${uid}`);

    await firebaseDatabaseService.updateDocument<User>("users", uid, {
        archivedAt: serverTimestamp(),
    });

    logger.debug(`[Members] Successfully archived member: ${uid}`);
}

async function startMemberLeave(uid: string, leaveReason: string): Promise<void> {
    const trimmed = leaveReason.trim();
    if (!trimmed) {
        throw new Error("Leave reason is required");
    }
    if (trimmed.length > USER_LEAVE_TEXT_MAX) {
        throw new Error(`Leave reason must be at most ${USER_LEAVE_TEXT_MAX} characters`);
    }
    logger.debug(`[Members] Starting leave for member: ${uid}`);

    await firebaseDatabaseService.updateDocument<User>("users", uid, {
        leaveReason: trimmed,
        leaveStartedAt: serverTimestamp(),
    });

    logger.debug(`[Members] Leave started for member: ${uid}`);
}

async function endMemberLeave(uid: string, member: User): Promise<void> {
    if (!member.leaveStartedAt) {
        throw new Error("Member is not on leave");
    }

    logger.debug(`[Members] Ending leave for member: ${uid}`);

    const period = {
        endedAt: Timestamp.now(),
        startedAt: Timestamp.fromDate(member.leaveStartedAt),
        ...(member.leaveReason?.trim() ? { reason: member.leaveReason.trim() } : {}),
    };

    await firebaseDatabaseService.updateDocument<User>("users", uid, {
        leaveHistory: arrayUnion(period),
        leaveReason: deleteField(),
        leaveStartedAt: deleteField(),
    });

    logger.debug(`[Members] Leave ended for member: ${uid}`);
}

const membersService = {
    archiveMember,
    createMember,
    endMemberLeave,
    getAllMembers,
    getMemberById,
    grantAccess,
    promoteToAdmin,
    revokeAccess,
    startMemberLeave,
    updateMemberBirthday,
    updateMemberDisplayName,
    updateMemberPhoneNumber,
};

export default membersService;
